// @ts-nocheck
/* Store Custom File */

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code - Start */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
// import Icon, { IconPresentationProps } from "@insite/mobius/Icon"; // Store Custom Code
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import TextSeparator, { TextSeparatorPresentationProps } from "@store/Library/Components/Toolkit/TextSeparator";
import { signInProps } from "@store/Themes/common/props/signIn";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { useIsNamedContext } from "@toolkit/Framework/Components/NamedContext";
import PersonAddLine from "@toolkit/Library/Components/Icons/PersonAdd/PersonAddLine";
import parse from "html-react-parser";
/* Valtech Custom Code - End */

/* Store Custom Code - Start */
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import User from "@store/Library/Components/Icons/User";
import UserPlus from "@store/Library/Components/Icons/UserPlus";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

const seperatorStyle = css`
    position: relative;
    display: flex;
    align-items: center;
    overflow: hidden;

    &::after {
        content: "";
        display: block;
        position: absolute;
        z-index: 1;
        border-color: ${getColor("common.border")};
        ${breakpoint("lg", "down")} {
            border-bottom-width: 1px;
            border-top-width: 0;
            border-left-width: 0;
            border-right-width: 0;
            height: 1px;
            left: 1rem;
            right: 1rem;
        }
        ${breakpoint("lg", "up")} {
            border-right-width: 1px;
            border-top-width: 0;
            border-left-width: 0;
            border-bottom-width: 0;
            height: 100%;
            width: 1px;
            left: 50%;
        }
    }

    span {
        position: relative;
        z-index: 2;
        display: inline-block;
        text-align: center;
        font-weight: bold;
        font-size: ${pxToRem(14)};
        text-transform: uppercase;
        background-color: ${getColor("common.background")};
        padding: ${pxToRem(10)};
        margin-left: auto;
        margin-right: auto;
        ${breakpoint("lg", "up")} {
            width: 100%;
        }
    }
`;
/* Store Custom Code - End */

const enum fields {
    text = "text",
    fullWidthButton = "fullWidthButton",
    // Valtech Custom Code
    title = "title",
    showIcon = "showIcon",
    // Store Custom Code
    guest = "guest",
}

const mapStateToProps = (state: ApplicationState) => {
    let { search } = getLocation(state);
    const { allowCreateAccount, allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }

    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        allowCreateAccount,
        allowGuestCheckout:
            allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (referredFromShipping || referredFromOnePageCheckout),
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.fullWidthButton]: boolean;
        // Valtech Custom Code
        [fields.showIcon]: boolean;
        [fields.title]: string;
        // Store Custom Code
        [fields.guest]: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: GridItemProps;
    signInCreateNewAccountButtonGridItem?: GridItemProps;
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    // Valtech Custom Code
    signInCreateNewAccountIcon?: IconProps; // Store Custom Code
    signInCreateNewAccountWrapper?: InjectableCss;
    separator?: TextSeparatorPresentationProps;
    // Store Custom Code
    signInCreateNewAccountSubContainer?: GridContainerProps;
    signInCreateNewAccountGridCell?: GridItemProps;
    signInCreateNewAccountGridSeperatorSolid?: GridItemProps;
    signInCreateNewAccountGridSeperatorDashed?: GridItemProps;
}

export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    /* Store Custom Code - Start */
    signInCreateNewAccountSubContainer: {
        css: css`
            margin-top: 2rem;
            margin-bottom: 2rem;
        `,
    },
    signInCreateNewAccountGridCell: {
        width: [12, 12, 12, 4, 4],
        css: css`
            flex-flow: column;
            ${breakpoint("xs", "down")} {
                div {
                    width: 100%;
                }
            }
        `,
    },
    signInCreateNewAccountGridSeperatorSolid: {
        width: [12, 12, 12, 2, 2],
        css: css`
            position: relative;
            ${seperatorStyle}
            ${breakpoint("md", "down")} {
                top: ${pxToRem(35)};
            }
            &::after {
                border-style: solid;
                ${breakpoint("md", "down")} {
                    top: 50%;
                }
            }
        `,
    },
    signInCreateNewAccountGridSeperatorDashed: {
        width: [12, 12, 12, 2, 2],
        css: css`
            ${seperatorStyle}
            &::after {
                border-style: dashed;
            }
        `,
    },
    signInCreateNewAccountIcon: {
        height: 47,
        width: 45,
        css: css`
            color: ${getColor("primary.main")};
            display: block;
        `,
    },
    /* Store Custom Code - End */
    signInCreateNewAccountTitle: {
        variant: "h4",
    },
    signInCreateNewAccountTitleGridItem: {
        width: 12,
        /* Store Custom Code - Start */
        css: css`
            display: block;
        `,
        /* /Store Custom Code - End */
    },
    signInCreateNewAccountTextGridItem: {
        width: 12,
    },
    signInCreateNewAccountButtonGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                        null,
                        css`
                            flex-direction: row;
                            justify-content: space-between;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    createNewAccountButton: {
        variant: "tertiary",
        /* Store Removed - css */
    },
    // Store Removed - createNewAccountButton
};

/**
 * @deprecated Use signInCreateNewAccountStyles instead.
 */
export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields: { fullWidthButton, showIcon, title, text, guest }, // Valtech Custom Code // Store Custom Code
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    signInAsGuest,
}) => {
    if (!allowCreateAccount) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    /* Store Custom Code - Start */
    return (
        <GridContainer>
            <GridItem {...styles.signInCreateNewAccountGridSeperatorSolid}>
                <span>{translate("OR")}</span>
            </GridItem>
            <GridItem {...styles.signInCreateNewAccountGridCell}>
                <GridContainer {...styles.signInCreateNewAccountSubContainer}>
                    <GridItem {...styles.signInCreateNewAccountTitleGridItem}>
                        <Icon src={UserPlus} {...styles.signInCreateNewAccountIcon} />
                        <Typography {...styles.signInCreateNewAccountTitle}>
                            {translate("Create New Account")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.signInCreateNewAccountTextGridItem}>
                        <Typography {...styles.signInCreateNewAccountText}>{text}</Typography>
                    </GridItem>
                </GridContainer>
                <FormActions>
                    {createAccountUrl && (
                        <Button
                            {...styles.createNewAccountButton}
                            onClick={handleCreateNewAccount}
                            data-test-selector="signInCreateNewAccount_createNewAccount"
                        >
                            {translate("Create Account")}
                        </Button>
                    )}
                </FormActions>
            </GridItem>
            {allowGuestCheckout && ( // Added in LSE-28
                <>
                    <GridItem {...styles.signInCreateNewAccountGridSeperatorDashed}></GridItem>
                    <GridItem {...styles.signInCreateNewAccountGridCell}>
                        <GridContainer {...styles.signInCreateNewAccountSubContainer}>
                            <GridItem {...styles.signInCreateNewAccountTitleGridItem}>
                                <Icon src={User} {...styles.signInCreateNewAccountIcon} />
                                <Typography {...styles.signInCreateNewAccountTitle}>
                                    {translate("Checkout as Guest")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.signInCreateNewAccountTextGridItem}>
                                <Typography {...styles.signInCreateNewAccountText}>{guest}</Typography>
                            </GridItem>
                        </GridContainer>
                        <FormActions>
                            <Button
                                {...styles.checkoutAsGuestButton}
                                onClick={handleCheckoutAsGuest}
                                data-test-selector="signInCreateNewAccount_checkoutAsGuest"
                            >
                                {translate("Checkout as Guest")}
                            </Button>
                        </FormActions>
                    </GridItem>
                </>
            )}
        </GridContainer>
    );
};
/* Store Custom Code - End */

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(SignInCreateNewAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            /* Store Custom Code - Removed showIcon */
            /* Store Custom Code - Removed title */
            {
                name: fields.text,
                displayName: "Create New Account Text", // Store Custom Code
                // Store Custom Code
                editorTemplate: "MultilineTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
            },
            /* Store Custom Code - Start */
            {
                name: fields.guest,
                displayName: "Guest Checkout Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
            },
            /* Store Custom Code - End */
            /* Store Custom Code - Removed fullWidthButton */
        ],
    },
};

export default widgetModule;
